import { apiClient } from '../api/apiClient';
import { API_ENDPOINTS } from '../api/apiEndpoints';
import { calendarDateFromApi } from '../utils/dateTime';
import type {
  LateArrivalConfiguration,
  LateArrivalConfigurationInput,
  LateArrivalDeduction,
  LateArrivalLeaveTypeOption,
  LateArrivalMonthSummary,
  LateArrivalRule,
  LateArrivalWorkPolicyThreshold,
} from '../types/lateArrivalConfiguration';

type Envelope<T> = { success: boolean; message: string; data: T };
type ObjectValue = Record<string, unknown>;

function bool(value: unknown): boolean {
  return value === true || value === 1 || value === '1';
}

function num(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function text(value: unknown): string {
  return typeof value === 'string' ? value : value == null ? '' : String(value);
}

function rows(value: unknown): ObjectValue[] {
  return Array.isArray(value)
    ? value.filter((row): row is ObjectValue => Boolean(row) && typeof row === 'object')
    : [];
}

export function emptyLateArrivalConfiguration(): LateArrivalConfiguration {
  return {
    lateArrivalConfigurationId: null,
    organizationId: null,
    isEnabled: false,
    rowVersion: 1,
    rules: [],
    leaveTypes: [],
    workPolicies: [],
  };
}

export function normalizeLateArrivalRule(row: ObjectValue): LateArrivalRule {
  return {
    lateArrivalRuleId: num(row.lateArrivalRuleId),
    occurrenceFrom: num(row.occurrenceFrom) ?? 1,
    occurrenceTo: num(row.occurrenceTo),
    leaveTypeId: num(row.leaveTypeId),
    leaveTypeCode: text(row.leaveTypeCode).trim(),
    leaveTypeName: text(row.leaveTypeName).trim(),
    deductionDays: num(row.deductionDays) ?? 0,
    lopOnInsufficientBalance: bool(row.lopOnInsufficientBalance),
  };
}

function normalizeLeaveType(row: ObjectValue): LateArrivalLeaveTypeOption | null {
  const leaveTypeId = num(row.leaveTypeId);
  if (leaveTypeId === null) return null;
  return {
    leaveTypeId,
    leaveTypeCode: text(row.leaveTypeCode).trim(),
    leaveTypeName: text(row.leaveTypeName).trim(),
  };
}

function normalizeWorkPolicy(row: ObjectValue): LateArrivalWorkPolicyThreshold | null {
  const workPolicyId = num(row.workPolicyId);
  if (workPolicyId === null) return null;
  return {
    workPolicyId,
    policyCode: text(row.policyCode).trim(),
    policyName: text(row.policyName).trim(),
    lateGraceMinutes: num(row.lateGraceMinutes),
    isDefault: bool(row.isDefault),
  };
}

export function normalizeLateArrivalConfiguration(
  row: ObjectValue | null | undefined,
): LateArrivalConfiguration {
  const fallback = emptyLateArrivalConfiguration();
  if (!row) return fallback;
  return {
    lateArrivalConfigurationId: num(row.lateArrivalConfigurationId),
    organizationId: num(row.organizationId),
    isEnabled: bool(row.isEnabled),
    rowVersion: num(row.rowVersion) ?? 1,
    rules: rows(row.rules)
      .map(normalizeLateArrivalRule)
      .sort((left, right) => left.occurrenceFrom - right.occurrenceFrom),
    leaveTypes: rows(row.leaveTypes)
      .map(normalizeLeaveType)
      .filter((item): item is LateArrivalLeaveTypeOption => item !== null),
    workPolicies: rows(row.workPolicies)
      .map(normalizeWorkPolicy)
      .filter((item): item is LateArrivalWorkPolicyThreshold => item !== null),
  };
}

export function normalizeLateArrivalDeduction(row: ObjectValue): LateArrivalDeduction {
  return {
    lateArrivalDeductionId: num(row.lateArrivalDeductionId),
    attendanceDate: calendarDateFromApi(row.attendanceDate),
    occurrenceNumber: num(row.occurrenceNumber) ?? 0,
    lateMinutes: num(row.lateMinutes) ?? 0,
    thresholdMinutes: num(row.thresholdMinutes) ?? 0,
    leaveTypeId: num(row.leaveTypeId),
    leaveTypeName: text(row.leaveTypeName).trim() || null,
    ruleDeductionDays: num(row.ruleDeductionDays) ?? 0,
    leaveDeductedDays: num(row.leaveDeductedDays) ?? 0,
    lopDays: num(row.lopDays) ?? 0,
  };
}

export function normalizeLateArrivalMonthSummary(
  row: ObjectValue | null | undefined,
  year: number,
  month: number,
): LateArrivalMonthSummary {
  const source = row ?? {};
  const nextRule =
    source.nextRule && typeof source.nextRule === 'object'
      ? normalizeLateArrivalRule(source.nextRule as ObjectValue)
      : null;
  return {
    year: num(source.year) ?? year,
    month: num(source.month) ?? month,
    isEnabled: bool(source.isEnabled),
    lateThresholdMinutes: num(source.lateThresholdMinutes),
    shiftStartTime: text(source.shiftStartTime).trim() || null,
    lateCount: num(source.lateCount) ?? 0,
    leaveDeductedDays: num(source.leaveDeductedDays) ?? 0,
    lopDays: num(source.lopDays) ?? 0,
    rules: rows(source.rules).map(normalizeLateArrivalRule),
    nextRule,
    items: rows(source.items).map(normalizeLateArrivalDeduction),
  };
}

export async function getLateArrivalConfiguration(): Promise<LateArrivalConfiguration> {
  const response = await apiClient.get<Envelope<ObjectValue>>(
    API_ENDPOINTS.HR_POLICY_CONFIGURATIONS.LATE_ARRIVAL,
  );
  return normalizeLateArrivalConfiguration(response.data.data);
}

export async function saveLateArrivalConfiguration(
  input: LateArrivalConfigurationInput,
): Promise<LateArrivalConfiguration> {
  const response = await apiClient.put<Envelope<ObjectValue>>(
    API_ENDPOINTS.HR_POLICY_CONFIGURATIONS.LATE_ARRIVAL,
    input,
  );
  return normalizeLateArrivalConfiguration(response.data.data);
}

export async function getLateArrivalMonthSummary(
  year: number,
  month: number,
  options: { employeeId?: number } = {},
): Promise<LateArrivalMonthSummary> {
  const params: Record<string, number> = { year, month };
  if (
    typeof options.employeeId === 'number' &&
    Number.isInteger(options.employeeId) &&
    options.employeeId > 0
  ) {
    params.employeeId = options.employeeId;
  }
  const response = await apiClient.get<Envelope<ObjectValue>>(
    API_ENDPOINTS.ATTENDANCE.LATE_ARRIVALS,
    { params },
  );
  return normalizeLateArrivalMonthSummary(response.data.data, year, month);
}
